import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import toast from 'react-hot-toast';
import {
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  PlayIcon,
  ClockIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  ShoppingCartIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';

const AnalyticsTest = () => {
  const { user } = useAuth();
  const { trackEvent, config, consentGiven } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [selectedTest, setSelectedTest] = useState('all');

  // Test configurations
  const testConfigs = {
    ga4: {
      name: 'Google Analytics 4',
      icon: ChartBarIcon,
      color: 'blue',
      tests: [
        { id: 'page_view', name: 'Page View', endpoint: '/api/admin/analytics/test-ga4' },
        { id: 'custom_event', name: 'Custom Event', endpoint: '/api/admin/analytics/test-ga4' }
      ]
    },
    facebook: {
      name: 'Facebook Conversions API',
      icon: CursorArrowRaysIcon,
      color: 'purple',
      tests: [
        { id: 'test_event', name: 'Test Event', endpoint: '/api/admin/analytics/test-facebook-pixel' },
        { id: 'view_content', name: 'View Content', endpoint: '/api/facebook-analytics/view-content' },
        { id: 'add_to_cart', name: 'Add to Cart', endpoint: '/api/facebook-analytics/add-to-cart' },
        { id: 'purchase', name: 'Purchase', endpoint: '/api/facebook-analytics/purchase' }
      ]
    },
    ecommerce: {
      name: 'E-commerce Events',
      icon: ShoppingCartIcon,
      color: 'green',
      tests: [
        { id: 'view_item', name: 'View Product', action: () => testViewItem() },
        { id: 'add_to_cart', name: 'Add to Cart', action: () => testAddToCart() },
        { id: 'add_to_wishlist', name: 'Add to Wishlist', action: () => testAddToWishlist() },
        { id: 'search', name: 'Search', action: () => testSearch() },
        { id: 'begin_checkout', name: 'Begin Checkout', action: () => testBeginCheckout() },
        { id: 'purchase', name: 'Purchase', action: () => testPurchase() }
      ]
    },
    user_actions: {
      name: 'User Actions',
      icon: UserPlusIcon,
      color: 'indigo',
      tests: [
        { id: 'sign_up', name: 'User Registration', action: () => testSignUp() },
        { id: 'login', name: 'User Login', action: () => testLogin() },
        { id: 'page_view', name: 'Page View', action: () => testPageView() }
      ]
    }
  };

  // E-commerce test functions
  const testViewItem = async () => {
    const mockProduct = {
      id: 'test-product-123',
      name: 'מוצר בדיקה',
      category: { name: 'בדיקות' },
      price_b2c: 99.99,
      sku: 'TEST-SKU-123'
    };

    await trackEvent('view_item', {
      item_id: mockProduct.id,
      item_name: mockProduct.name,
      item_category: mockProduct.category.name,
      price: mockProduct.price_b2c,
      currency: 'ILS'
    });

    return { success: true, message: 'אירוע צפייה במוצר נשלח בהצלחה' };
  };

  const testAddToCart = async () => {
    const mockProduct = {
      id: 'test-product-456',
      name: 'מוצר עגלה',
      category: { name: 'בדיקות' },
      price_b2c: 149.99,
      sku: 'CART-TEST-456'
    };

    await trackEvent('add_to_cart', {
      item_id: mockProduct.id,
      item_name: mockProduct.name,
      item_category: mockProduct.category.name,
      price: mockProduct.price_b2c,
      quantity: 2,
      value: mockProduct.price_b2c * 2,
      currency: 'ILS'
    });

    return { success: true, message: 'אירוע הוספה לעגלה נשלח בהצלחה' };
  };

  const testAddToWishlist = async () => {
    await trackEvent('add_to_wishlist', {
      item_id: 'wishlist-test-789',
      item_name: 'מוצר רשימת משאלות',
      item_category: 'בדיקות',
      price: 79.99,
      currency: 'ILS'
    });

    return { success: true, message: 'אירוע הוספה לרשימת משאלות נשלח בהצלחה' };
  };

  const testSearch = async () => {
    await trackEvent('search', {
      search_term: 'בדיקת חיפוש',
      results_count: 15
    });

    return { success: true, message: 'אירוע חיפוש נשלח בהצלחה' };
  };

  const testBeginCheckout = async () => {
    await trackEvent('begin_checkout', {
      value: 299.98,
      currency: 'ILS',
      items: [
        { item_id: 'checkout-1', item_name: 'מוצר 1', price: 149.99, quantity: 1 },
        { item_id: 'checkout-2', item_name: 'מוצר 2', price: 149.99, quantity: 1 }
      ]
    });

    return { success: true, message: 'אירוע תחילת קנייה נשלח בהצלחה' };
  };

  const testPurchase = async () => {
    await trackEvent('purchase', {
      transaction_id: `TEST-${Date.now()}`,
      value: 299.98,
      currency: 'ILS',
      tax: 51.00,
      shipping: 20.00,
      items: [
        { item_id: 'purchase-1', item_name: 'מוצר נרכש 1', price: 149.99, quantity: 1 },
        { item_id: 'purchase-2', item_name: 'מוצר נרכש 2', price: 149.99, quantity: 1 }
      ]
    });

    return { success: true, message: 'אירוע רכישה נשלח בהצלחה' };
  };

  const testSignUp = async () => {
    await trackEvent('sign_up', {
      method: 'email',
      user_id: 'test-user-signup'
    });

    return { success: true, message: 'אירוע הרשמה נשלח בהצלחה' };
  };

  const testLogin = async () => {
    await trackEvent('login', {
      method: 'email',
      user_id: user?.id || 'test-user'
    });

    return { success: true, message: 'אירוע התחברות נשלח בהצלחה' };
  };

  const testPageView = async () => {
    await trackEvent('page_view', {
      page_title: 'דף בדיקת אנליטיקה',
      page_location: window.location.href,
      page_path: '/admin/analytics-test'
    });

    return { success: true, message: 'אירוע צפייה בדף נשלח בהצלחה' };
  };

  // Run server-side test
  const runServerTest = async (testConfig) => {
    try {
      setLoading(true);
      const response = await axios.post(testConfig.endpoint, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      return {
        success: true,
        message: response.data.message || 'בדיקה הושלמה בהצלחה',
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'שגיאה בבדיקה',
        error: error.message
      };
    } finally {
      setLoading(false);
    }
  };

  // Run client-side test
  const runClientTest = async (testConfig) => {
    try {
      setLoading(true);
      const result = await testConfig.action();
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'שגיאה בביצוע בדיקה',
        error: error.message
      };
    } finally {
      setLoading(false);
    }
  };

  // Run individual test
  const runTest = async (categoryKey, testConfig) => {
    const testKey = `${categoryKey}_${testConfig.id}`;
    
    setTestResults(prev => ({
      ...prev,
      [testKey]: { loading: true }
    }));

    let result;
    if (testConfig.endpoint) {
      result = await runServerTest(testConfig);
    } else if (testConfig.action) {
      result = await runClientTest(testConfig);
    }

    setTestResults(prev => ({
      ...prev,
      [testKey]: {
        ...result,
        timestamp: new Date().toISOString(),
        loading: false
      }
    }));

    if (result.success) {
      toast.success(`${testConfig.name}: ${result.message}`);
    } else {
      toast.error(`${testConfig.name}: ${result.message}`);
    }
  };

  // Run all tests in category
  const runCategoryTests = async (categoryKey) => {
    const category = testConfigs[categoryKey];
    for (const test of category.tests) {
      await runTest(categoryKey, test);
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // Run all tests
  const runAllTests = async () => {
    for (const categoryKey of Object.keys(testConfigs)) {
      await runCategoryTests(categoryKey);
    }
  };

  const getTestResult = (categoryKey, testId) => {
    return testResults[`${categoryKey}_${testId}`];
  };

  const getStatusIcon = (result) => {
    if (result?.loading) {
      return <ClockIcon className="h-5 w-5 text-yellow-500 animate-spin" />;
    }
    if (result?.success) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    if (result?.success === false) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    return <PlayIcon className="h-5 w-5 text-gray-400" />;
  };

  const getStatusColor = (result) => {
    if (result?.loading) return 'yellow';
    if (result?.success) return 'green';
    if (result?.success === false) return 'red';
    return 'gray';
  };

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg" dir="rtl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <BeakerIcon className="h-8 w-8 ml-2 text-blue-600" />
          בדיקת מערכות אנליטיקה
        </h1>
        <p className="text-gray-600">
          בדוק את תקינות מערכות האנליטיקה ושלח אירועי בדיקה לכל הפלטפורמות.
        </p>
      </div>

      {/* Status Overview */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center mb-2">
          <InformationCircleIcon className="h-5 w-5 ml-2 text-blue-500" />
          <span className="font-medium text-blue-800">סטטוס מערכות</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full ml-2 ${config?.analytics_enabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>אנליטיקה כללית: {config?.analytics_enabled ? 'פעיל' : 'מושבת'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full ml-2 ${config?.ga4?.enabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>GA4: {config?.ga4?.enabled ? 'פעיל' : 'מושבת'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full ml-2 ${config?.facebook_pixel?.enabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Facebook Pixel: {config?.facebook_pixel?.enabled ? 'פעיל' : 'מושבת'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full ml-2 ${consentGiven ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>הסכמת משתמש: {consentGiven ? 'ניתנה' : 'לא ניתנה'}</span>
          </div>
        </div>
      </div>

      {/* Test Controls */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={runAllTests}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <PlayIcon className="h-4 w-4 ml-2" />
          הרץ את כל הבדיקות
        </button>
        
        {Object.entries(testConfigs).map(([key, category]) => (
          <button
            key={key}
            onClick={() => runCategoryTests(key)}
            disabled={loading}
            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-${category.color}-600 hover:bg-${category.color}-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${category.color}-500 disabled:opacity-50`}
          >
            <category.icon className="h-4 w-4 ml-2" />
            בדוק {category.name}
          </button>
        ))}
      </div>

      {/* Test Categories */}
      <div className="space-y-6">
        {Object.entries(testConfigs).map(([categoryKey, category]) => (
          <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className={`bg-${category.color}-50 border-b border-${category.color}-200 px-4 py-3`}>
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <category.icon className="h-6 w-6 ml-2" />
                {category.name}
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.tests.map((test) => {
                  const result = getTestResult(categoryKey, test.id);
                  const statusColor = getStatusColor(result);
                  
                  return (
                    <div
                      key={test.id}
                      className={`border-2 border-${statusColor}-200 bg-${statusColor}-50 rounded-lg p-4`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-800">{test.name}</h4>
                        {getStatusIcon(result)}
                      </div>
                      
                      {result?.message && (
                        <p className={`text-sm text-${statusColor}-700 mb-2`}>
                          {result.message}
                        </p>
                      )}
                      
                      {result?.timestamp && (
                        <p className="text-xs text-gray-500 mb-2">
                          {new Date(result.timestamp).toLocaleString('he-IL')}
                        </p>
                      )}
                      
                      <button
                        onClick={() => runTest(categoryKey, test)}
                        disabled={loading || result?.loading}
                        className={`w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-${category.color}-600 hover:bg-${category.color}-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${category.color}-500 disabled:opacity-50`}
                      >
                        {result?.loading ? (
                          <>
                            <ClockIcon className="h-4 w-4 ml-2 animate-spin" />
                            בודק...
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-4 w-4 ml-2" />
                            הרץ בדיקה
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Results Summary */}
      {Object.keys(testResults).length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-4">סיכום תוצאות</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {Object.values(testResults).filter(r => r.success === true).length}
              </div>
              <div className="text-gray-600">בדיקות עברו בהצלחה</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {Object.values(testResults).filter(r => r.success === false).length}
              </div>
              <div className="text-gray-600">בדיקות נכשלו</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {Object.values(testResults).filter(r => r.loading).length}
              </div>
              <div className="text-gray-600">בדיקות בביצוע</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTest;